import Header from "../../Layouts/Components/Header";
import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useKeycloak } from "@react-keycloak/web";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import { Grid, Typography, Divider } from "@mui/material";

import LockIcon from "@mui/icons-material/Lock";

import { customizeModules, getLicense, resetLicense } from "./state/actions";
import CustomButton from "../shared/CustomButton";
import CustomOnOffSwitch from "../shared/CustomOnOffSwitch";
import ScreenLoader from "../shared/ScreenLoader";
import useSnackBar from "../Hooks/useSnackBar";
import usePrompt from "../Hooks/usePrompt";
import SaveChangesModal from "../shared/SaveChangesModal";

const CustomizeModules = () => {
  const dispatch = useDispatch();
  const { uniqueId, allowedModules, processing, loadingLicense } = useSelector(
    (state) => state.license
  );
  const { keycloak } = useKeycloak();
  const Navigate = useNavigate();
  const [touched, setTouched] = useState(false);
  const { success, error, snackbar } = useSnackBar();

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [pendingLocation, setPendingLocation] = useState(null);

  const loadLicense = useCallback(
    () => dispatch(getLicense(keycloak.tokenParsed["license-key"])),
    [dispatch, keycloak]
  );
  const reset = useCallback(() => dispatch(resetLicense()), [dispatch]);
  const navigate = useCallback((location) => Navigate(location), [Navigate]);

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: {
      modules: allowedModules,
    },
    onSubmit: (values) => {
      const customizedModules = values.modules.map((module) => ({
        id: module.module.id,
        isActive: module.isActive,
      }));

      dispatch(
        customizeModules(uniqueId, customizedModules, success, error)
      ).then(() => {
        setTouched(false);
        setIsConfirmationModalOpen(false);
      });
    },
  });

  //change module handler
  const handleChangeModule = (e) => {
    const moduleId = e.target.id;
    const isActive = e.target.value === "false" ? false : true;
    const newModules = [...formik.values.modules].map((module) => {
      if (`${module.module.id}` === moduleId)
        return { ...module, isActive: !isActive };
      else return module;
    });

    formik.setFieldValue("modules", newModules);

    if (!touched) setTouched(true);
  };

  const handleBlockedNavigation = useCallback(
    (nextLocation) => {
      if (nextLocation.pathname === "/license/modules") return true;
      if (touched) {
        setPendingLocation(nextLocation.pathname);
        setIsConfirmationModalOpen(true);
        return false;
      }
      return true;
    },
    [touched]
  );

  //close navigation blocker handler
  const handleCloseConfirmationModal = () => {
    setIsConfirmationModalOpen(false);
    setPendingLocation(null);
  };

  //discard changes action handler
  const handleDiscardChanges = () => {
    setIsConfirmationModalOpen(false);
    setTouched(false);
  };

  //navigate to pendingLocation effect
  useEffect(() => {
    if (pendingLocation && !touched) navigate(pendingLocation);
  }, [navigate, pendingLocation, touched]);

  useEffect(() => {
    loadLicense();
    return () => {
      reset();
    };
  }, [loadLicense, reset]);

  //Navigation blocker
  usePrompt(handleBlockedNavigation, touched);

  return (
    <div>
      {snackbar}

      <SaveChangesModal
        open={isConfirmationModalOpen}
        onClose={handleCloseConfirmationModal}
        processing={processing}
        discardChanges={handleDiscardChanges}
        saveChanges={formik.handleSubmit}
      />
      <Header headLine="License" backButton={true}>
        <CustomButton
          text="Save"
          processing={processing}
          disabled={!touched}
          action={formik.handleSubmit}
        />
      </Header>
      {loadingLicense ? (
        <ScreenLoader />
      ) : (
        <Grid
          container
          justifyContent="center"
          sx={{
            background: "#ffffff",
            borderRadius: "6px",
            border: "1px solid #E7E9EF",
            margin: "30px",
            padding: "25px",
            width: "70%",
          }}
          direction="column"
        >
          <Typography
            sx={{
              color: "#050D2E",
              fontSize: "1rem",
              fontWeight: 550,
              lineHeight: "17px",
              marginBottom: "15px",
            }}
          >
            Customize Modules
          </Typography>

          {/*Modules */}
          <Grid item container direction="column" p={2}>
            {formik.values.modules?.map((module, i) => {
              return (
                <div key={module.module.id}>
                  <Grid container>
                    <Grid item xs={9}>
                      <Typography
                        sx={{
                          color: "#050D2E",
                          fontSize: ".875rem",
                          fontWeight: 500,
                          letterSpacing: "-0.53px",
                          lineHeight: "19px",
                        }}
                      >
                        {module.module.name}
                      </Typography>
                      <Typography
                        sx={{
                          color: "#8792AB",
                          fontSize: ".875rem",
                          fontWeight: 500,
                          lineHeight: "20px",
                          pb: 2,
                        }}
                      >
                        {module.module.description}
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      xs={3}
                      sx={{
                        display: "flex",
                        justifyContent: "end",
                      }}
                    >
                      {module.module.isLocked ? (
                        <LockIcon
                          sx={{
                            color: "#50566E",
                            fontSize: "1.3rem",
                            marginRight: "10px",
                            mt: 1,
                          }}
                        />
                      ) : null}
                      <CustomOnOffSwitch
                        id={`${module.module.id}`}
                        checked={module.isActive || false}
                        value={module.isActive || false}
                        disabled={module.module.isLocked}
                        onChange={handleChangeModule}
                      />
                    </Grid>
                  </Grid>
                  {i !== allowedModules.length - 1 && (
                    <Divider sx={{ mb: 2 }} />
                  )}
                </div>
              );
            })}
          </Grid>
        </Grid>
      )}
    </div>
  );
};

export default CustomizeModules;
